import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import { Visibility, Person, CalendarToday, Close, Delete } from '@mui/icons-material';
import { WriteoffNoteView } from '../../../../../services/WriteoffService/types/writeoffTypes';
import { useNoteActions } from './hooks';

export interface ViewNoteDialogProps {
  open: boolean;
  note: WriteoffNoteView | null;
  onClose: () => void;
}

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const ViewNoteDialog: React.FC<ViewNoteDialogProps> = ({ open, note, onClose }) => {
  const { canDelete, handleDelete } = useNoteActions({ note, onClose });

  if (!note) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Visibility sx={{ color: '#3b82f6' }} />
        View Note Details
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Note Metadata */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Chip
            icon={<Person />}
            label={note.addedBy || 'Unknown User'}
            variant="outlined"
            sx={{
              borderColor: canDelete ? '#10b981' : '#3b82f6',
              color: canDelete ? '#10b981' : '#3b82f6',
              '& .MuiChip-icon': {
                color: canDelete ? '#10b981' : '#3b82f6',
              },
            }}
          />
          <Chip
            icon={<CalendarToday />}
            label={formatDate(note.addDate)}
            variant="outlined"
            sx={{
              borderColor: '#64748b',
              color: '#64748b',
              '& .MuiChip-icon': {
                color: '#64748b',
              },
            }}
          />
        </Stack>

        {/* Note Content */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 1.5,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6,
            }}
          >
            {note.text}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          {canDelete && (
            <Button
              onClick={handleDelete}
              variant="outlined"
              startIcon={<Delete />}
              color="error"
              sx={{
                borderColor: '#fca5a5',
                color: '#dc2626',
                '&:hover': {
                  borderColor: '#dc2626',
                  backgroundColor: '#fef2f2',
                },
              }}
            >
              Delete Note
            </Button>
          )}
        </Box>
        <Button
          onClick={onClose}
          variant="contained"
          startIcon={<Close />}
          sx={{
            backgroundColor: '#162346',
            '&:hover': {
              backgroundColor: '#1a2a52',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewNoteDialog;
